import React from 'react'
import { useLocalstorageState, useOutsideClick } from 'rooks'
import { useIsBetLoopSportsFeatureActive } from '../useIsBetLoopSportsFeatureActive'

const defaultState: TooltipState = { seenCount: 0, lastShownAt: null }

export const useBetLoopSportTooltips = (
  tooltipStorageKey: string,
  isEnabled = true,
  tooltipRef: React.RefObject<HTMLDivElement | HTMLButtonElement | null>,
  coolDownMs: number = 24 * 60 * 60 * 1000 // 24h
) => {
  const isBetLoopSportsFeatureActive = useIsBetLoopSportsFeatureActive()

  const [isSportTooltipVisible, setIsSportTooltipVisible] = React.useState(false)
  const [tooltipState, setTooltipState] = useLocalstorageState<TooltipState>(
    tooltipStorageKey,
    defaultState
  )
  const tooltipSeenRef = React.useRef(false)

  React.useEffect(() => {
    if (!isBetLoopSportsFeatureActive || !isEnabled) return
    if (tooltipSeenRef.current) return

    const now = Date.now()
    const { seenCount, lastShownAt } = tooltipState ?? defaultState

    const canShow =
      seenCount === 0 || (seenCount === 1 && lastShownAt != null && now - lastShownAt >= coolDownMs)

    if (!canShow) return

    tooltipSeenRef.current = true
    setIsSportTooltipVisible(true)

    setTooltipState(prev => {
      const prevState = prev ?? defaultState
      const nextCount = Math.min(2, prevState.seenCount + 1) as 0 | 1 | 2
      return { seenCount: nextCount, lastShownAt: now }
    })
  }, [isBetLoopSportsFeatureActive, isEnabled, coolDownMs, tooltipState, setTooltipState])

  const handleDismissTooltip = () => {
    setIsSportTooltipVisible(false)
  }

  useOutsideClick(tooltipRef, () => {
    if (isSportTooltipVisible) handleDismissTooltip()
  })

  const onTooltipClick = React.useCallback(() => {
    setTooltipState(prev => ({ ...(prev ?? defaultState), seenCount: 2 }))
    setIsSportTooltipVisible(false)
  }, [setTooltipState])

  return {
    tooltipRef,
    onTooltipClick,
    handleDismissTooltip,
    isSportTooltipVisible,
  }
}

type TooltipState = {
  seenCount: 0 | 1 | 2
  lastShownAt: number | null
}
